import {
  FC,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSnackbar } from 'notistack';

import { useRequest } from '~hooks/useRequest';
import { useInterval } from '~hooks/useInterval';
import {
  getCollectedItems,
  getCollectedItemsByDriver,
  getCollectedItemsByZone,
  getCollectedItemsByCompany,
} from '~services/route/report';
import {
  RouteReportCollectedItemsMapItem,
  RouteReportCollectedItemsResponse,
  RouteReportCollectedItemsByDriverResponse,
  RouteReportCollectedItemsByZoneResponse,
  RouteReportCollectedItemsByCompanyResponse,
} from '~services/route/report/types';
import { LogisticZoneMap } from '~services/logisticsZones/types';
import { Nullable } from '~globals/types/commons';
import { LogisticZoneType } from '~globals/types/enums';
import { DEFAULT_UUIID_EMPTY } from '~constants/commons';

import { useDashboardVariantV2CarrierContentCollectTabContext } from '../../Context';

import {
  DashboardVariantV2CarrierContentCollectTrackingContextValue,
  DashboardVariantV2CarrierContentCollectTrackingPanelId,
} from './types';
import {
  getReportCollectedItemsModeByTabId,
  sortTrackingDataResponse,
} from './utils';

const DEFAULT_INTERVAL_RELOAD_DATA = 5000; //@TODO: UI V2: Verify

const DashboardVariantV2CarrierContentCollectTrackingContext =
  createContext<DashboardVariantV2CarrierContentCollectTrackingContextValue>({
    activeTab: {
      tabId: 'drivers',
      setTabId: () => {},
    },
    tabsData: {
      data: null,
      isFirstRequest: true,
      loading: true,
      selectedDriverId: {
        value: null,
        setValue: () => {},
      },
      selectedZoneId: {
        value: null,
        setValue: () => {},
      },
      selectedCompany: {
        value: null,
        setValue: () => {},
      },
    },
    mapData: {
      isFirstRequest: true,
      loading: true,
      items: [],
      logisticZones: [],
    },
  });

export const useDashboardVariantV2CarrierContentCollectTrackingContext =
  (): DashboardVariantV2CarrierContentCollectTrackingContextValue =>
    useContext(DashboardVariantV2CarrierContentCollectTrackingContext);

const DashboardVariantV2CarrierContentCollectTrackingProvider: FC = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    isOnlyTodayDelivery: { value: isOnlyTodayDelivery },
  } = useDashboardVariantV2CarrierContentCollectTabContext();

  const [activeTabId, setActiveTabId] =
    useState<DashboardVariantV2CarrierContentCollectTrackingPanelId>('drivers');

  const isSelectedDriversTab = useMemo(
    () => activeTabId === 'drivers',
    [activeTabId],
  );
  const isSelectedZonesTab = useMemo(
    () => activeTabId === 'zones',
    [activeTabId],
  );
  const isSelectedCompaniesTab = useMemo(
    () => activeTabId === 'companies',
    [activeTabId],
  );

  const [selectedDriverId, setSelectedDriverId] =
    useState<Nullable<string>>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<Nullable<string>>(null);
  const [selectedCompany, setSelectedCompany] =
    useState<Nullable<{ companyId: string; giverCompanyId: string }>>(null);

  const [globalReloadCount, setGlobalReloadCount] = useState(0);

  useInterval(() => {
    setGlobalReloadCount((prevCount) => prevCount + 1);
  }, DEFAULT_INTERVAL_RELOAD_DATA);

  const [isFirstRequestTabData, setIsFirstRequestTabData] = useState(true);
  const [tabsData, setTabsData] =
    useState<Nullable<RouteReportCollectedItemsResponse>>(null);

  const reportCollectedItemsMode = useMemo(
    () => getReportCollectedItemsModeByTabId(activeTabId),
    [activeTabId],
  );

  const [loadingTabData, setLoadingTabData] = useState(true);
  useRequest(
    {
      request: getCollectedItems,
      payload: {
        reportCollectedItemsMode,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const trackingDataResponse = response.data
          ?.data as RouteReportCollectedItemsResponse;

        const trackingDataResponseSorted =
          sortTrackingDataResponse(trackingDataResponse);

        setTabsData(trackingDataResponseSorted);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestTabData(false);
        setLoadingTabData(false);
      },
    },
    [reportCollectedItemsMode, isOnlyTodayDelivery, globalReloadCount],
  );

  const [isFirstRequestMapData, setIsFirstRequestMapData] = useState(true);
  const [mapItems, setMapItems] = useState<RouteReportCollectedItemsMapItem[]>(
    [],
  );
  const [mapLogisticZones, setMapLogisticZones] = useState<LogisticZoneMap[]>(
    [],
  );

  const [loadingMapDataByDriver, setLoadingMapDataByDriver] =
    useState(isSelectedDriversTab);
  useRequest(
    {
      request: getCollectedItemsByDriver,
      isValidToRequest: isSelectedDriversTab,
      payload: {
        driverId: selectedDriverId,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const { items } = response.data
          ?.data as RouteReportCollectedItemsByDriverResponse;

        const itemsMapped = selectedDriverId ? items : [];

        setMapItems(itemsMapped);
        setMapLogisticZones([]);

        // Force Reload without items for selectedDriverId
        if (selectedDriverId && itemsMapped.length === 0) {
          setSelectedDriverId(null);
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapDataByDriver(false);
      },
    },
    [activeTabId, selectedDriverId, isOnlyTodayDelivery, globalReloadCount],
  );

  const [loadingMapDataByZone, setLoadingMapDataByZone] =
    useState(isSelectedZonesTab);
  useRequest(
    {
      request: getCollectedItemsByZone,
      isValidToRequest: isSelectedZonesTab,
      payload: {
        zoneId: selectedZoneId,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const { items, zones } = response.data
          ?.data as RouteReportCollectedItemsByZoneResponse;

        const filterZones = zones.filter(
          (zone) => zone.zoneTypeId === LogisticZoneType.GeometryArea,
        );

        setMapItems(items);
        setMapLogisticZones(filterZones);

        // Force Reload without items for selectedZoneId
        if (selectedZoneId && items.length === 0) {
          setSelectedZoneId(null);
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapDataByZone(false);
      },
    },
    [activeTabId, selectedZoneId, isOnlyTodayDelivery, globalReloadCount],
  );

  const [loadingMapDataByCompany, setLoadingMapDataByCompany] = useState(
    isSelectedCompaniesTab,
  );
  useRequest(
    {
      request: getCollectedItemsByCompany,
      isValidToRequest: isSelectedCompaniesTab,
      initialLoading: isSelectedCompaniesTab,
      payload: {
        companyId:
          selectedCompany?.companyId === DEFAULT_UUIID_EMPTY
            ? selectedCompany.giverCompanyId
            : selectedCompany?.companyId ?? null,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const { items } = response.data
          ?.data as RouteReportCollectedItemsByCompanyResponse;

        setMapItems(items);
        setMapLogisticZones([]);

        // Force Reload without items for selectedCompany
        if (selectedCompany && items.length === 0) {
          setSelectedCompany(null);
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapDataByCompany(false);
      },
    },
    [
      activeTabId,
      selectedCompany?.companyId,
      selectedCompany?.giverCompanyId,
      isOnlyTodayDelivery,
      globalReloadCount,
    ],
  );

  const handleChangeActiveTabIdWithLoading = useCallback(
    (newTabId: DashboardVariantV2CarrierContentCollectTrackingPanelId) => {
      setLoadingTabData(true);

      setActiveTabId(newTabId);

      setSelectedDriverId(null);
      setSelectedZoneId(null);
      setSelectedCompany(null);
    },
    [],
  );

  const handleChangeSelectedDriverId = useCallback(
    (newDriverId: Nullable<string>) => {
      setLoadingMapDataByDriver(true);

      setSelectedDriverId(newDriverId);
    },
    [],
  );

  const handleChangeSelectedZoneId = useCallback(
    (newZoneId: Nullable<string>) => {
      setLoadingMapDataByZone(true);

      setSelectedZoneId(newZoneId);
    },
    [],
  );

  const handleChangeSelectedCompany = useCallback(
    (newCompany: Nullable<{ companyId: string; giverCompanyId: string }>) => {
      setLoadingMapDataByCompany(true);

      setSelectedCompany(newCompany);
    },
    [],
  );

  const loadingMapData = useMemo(
    () =>
      loadingMapDataByDriver || loadingMapDataByZone || loadingMapDataByCompany,
    [loadingMapDataByDriver, loadingMapDataByZone, loadingMapDataByCompany],
  );

  return (
    <DashboardVariantV2CarrierContentCollectTrackingContext.Provider
      value={{
        activeTab: {
          tabId: activeTabId,
          setTabId: handleChangeActiveTabIdWithLoading,
        },
        tabsData: {
          data: tabsData,
          isFirstRequest: isFirstRequestTabData,
          loading: loadingTabData,
          selectedDriverId: {
            value: selectedDriverId,
            setValue: handleChangeSelectedDriverId,
          },
          selectedZoneId: {
            value: selectedZoneId,
            setValue: handleChangeSelectedZoneId,
          },
          selectedCompany: {
            value: selectedCompany,
            setValue: handleChangeSelectedCompany,
          },
        },
        mapData: {
          isFirstRequest: isFirstRequestMapData,
          loading: loadingMapData,
          items: mapItems,
          logisticZones: mapLogisticZones,
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContentCollectTrackingContext.Provider>
  );
};

export default DashboardVariantV2CarrierContentCollectTrackingProvider;
